const numberFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

const intFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function fmtPrice(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  return numberFormatter.format(n);
}

/**
 * Format a plain ISO date (yyyy-MM-dd) as TT.MM.JJ for the price table's
 * "Datum" column. Pure string manipulation — no Date/timezone math — so the
 * shown day never drifts across a UTC/Berlin boundary.
 */
export function fmtDateShort(iso: string | null): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1].slice(2)}`;
}

export function fmtChange(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return sign + numberFormatter.format(n);
}

/**
 * Parse a Montel `UpdateTime` (or any similarly-shaped naked-ISO timestamp)
 * into a real `Date` instant.
 *
 * Montel emits these without a `Z` suffix and without an explicit offset:
 *   "2026-06-01T11:17:00.0000000"  →  meant as 11:17 Europe/Berlin
 *
 * Naïvely appending `Z` interprets the value as UTC, which then gets
 * re-rendered as 13:17 in Europe/Berlin — a +2 h drift during CEST and
 * +1 h during CET. We compensate by parsing as UTC, asking the engine
 * what UTC offset Berlin had at that moment, and shifting the result so
 * the absolute instant matches the intended wall-clock time.
 *
 * Strings that already carry a `Z` suffix or an explicit `±HH:MM` offset
 * are trusted as-is.
 */
function parseMontelLocalDate(iso: string): Date | null {
  if (/[zZ]|[+\-]\d{2}:?\d{2}$/.test(iso)) {
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const asUtc = new Date(iso + "Z");
  if (!Number.isFinite(asUtc.getTime())) return null;
  const offsetMin = berlinOffsetMinutes(asUtc);
  return new Date(asUtc.getTime() - offsetMin * 60_000);
}

function berlinOffsetMinutes(d: Date): number {
  // Returns the Europe/Berlin UTC offset in minutes for the given instant
  // (60 in CET, 120 in CEST). Falls back to CET on Intl errors.
  if (!Number.isFinite(d.getTime())) return 60;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const lookup: Record<string, string> = {};
    for (const p of parts) if (p.type !== "literal") lookup[p.type] = p.value;
    const asUtc = Date.UTC(
      Number(lookup.year),
      Number(lookup.month) - 1,
      Number(lookup.day),
      Number(lookup.hour === "24" ? "00" : lookup.hour),
      Number(lookup.minute),
      Number(lookup.second),
    );
    return Math.round((asUtc - d.getTime()) / 60_000);
  } catch {
    return 60;
  }
}

/**
 * Compact per-row update timestamp for the price table. Shows `HH:MM` if the
 * timestamp is from today (most rows), `DD.MM. HH:MM` for older values. Full
 * datetime is exposed via the cell's `title` attribute.
 */
export function fmtUpdateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = parseMontelLocalDate(iso);
    if (!d) return iso;
    // "Today" comparison must use the Europe/Berlin calendar day, not UTC —
    // 23:30 Berlin on day N is still day N for our colleagues even though
    // it's 22:30 UTC, the same calendar day, but for the inverse case
    // (00:30 Berlin on day N+1 = 22:30 UTC day N) the UTC compare would
    // wrongly classify it as the previous day.
    const dBerlin = berlinYmd(d);
    const nowBerlin = berlinYmd(new Date());
    const sameDay = dBerlin === nowBerlin;
    return d.toLocaleString("de-DE", {
      ...(sameDay
        ? { hour: "2-digit", minute: "2-digit" }
        : { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }),
      timeZone: "Europe/Berlin",
    });
  } catch {
    return iso;
  }
}

/** Full datetime tooltip (DD.MM.YYYY HH:MM) for the price-table title attr. */
export function fmtUpdateTimeFull(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = parseMontelLocalDate(iso);
    if (!d) return iso;
    return d.toLocaleString("de-DE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Berlin",
    });
  } catch {
    return iso;
  }
}

function berlinYmd(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

export function fmtRelative(iso: string | null): string {
  if (!iso) return "noch nie";
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "noch nie";
  const diffSec = Math.round((Date.now() - ts) / 1000);
  if (diffSec < 5) return "gerade eben";
  if (diffSec < 60) return `vor ${diffSec} s`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  return `vor ${h} h`;
}

export { intFormatter };
